import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ListingContext from '../../providers/Listing/Listing.context';
import { Button, Loader } from 'ncoded-component-library';
import { Form } from 'react-final-form';
import useMultiStepFormPatching from 'hooks/useMultiStepFormPatching';
import { BossModeSkipPayment, Listing } from 'models/Listing';
import FloatingActions from 'components/FloatingActions';
import arrayMutators from 'final-form-arrays';
import StepsContext from 'providers/Steps/Steps.context';
import {
  HIDE_CONTROLS_PAGES,
  STEP_NAMES,
  LCW_STEP_SUBMIT_TEXT,
  validateLCW,
  processLCWValuesBeforeSubmit,
} from '../../utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import utils, {
  checkIsUrgent,
  getListingTypeLabel,
  getSellingPortfolio,
  scrollToErrorsOnSubmit,
  sentryError,
} from 'utils';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useOnClose from '../../hooks/useOnClose';
import { PACKAGES_WITHOUT_MEDIA } from 'providers/Steps/Steps.provider';
import { GET_BUYERS_OPTIONS } from '../GetBuyers/GetBuyers.component';
import { BUSINESS_ROUTE } from 'data/global';
import BmSkipPaymentModal from './components/BmSkipPaymentModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import { ListingCreationInitiatedEventProps } from 'models/MixpanelEvents';

import './LcwStepWrapper.styles.scss';

type LcwStepWrapperProps = {
  className?: string;
};

export const GET_BUYERS_STEP = 2;
export const CHECKOUT_STEP = 4;

const LcwStepWrapper: React.FC<LcwStepWrapperProps> = (props) => {
  const { children, className } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    isSuperAdmin,
    currentUser: { canSkipPayment },
  } = useContext(CurrentUserContext);
  const { currentStep, steps, nextStep, prevStep, goToStep } =
    useContext(StepsContext);
  const {
    listingFormValues,
    listingType,
    dealName,
    listing: {
      _id: listingId,
      dealRoomId,
      listingPackage,
      industry,
      mediaPackage,
      buyerOutreach,
      listingTitle,
    },
    patchListingStep,
    submitListing,
  } = useContext(ListingContext);

  const formValues = useRef<Record<string, any>>();
  const modSinceLastSubmit = useRef(false);
  const resetModified = useRef(false);

  const [bmSkipPaymentModal, bmSkipPaymentModalRef] =
    useCallbackRef<ModalRef>();

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const hideControls = useMemo(
    () => HIDE_CONTROLS_PAGES.some((el) => pathname.includes(el)),
    [pathname],
  );

  const isLastStep = useMemo(
    () => currentStep === steps.length - 1,
    [currentStep, steps.length],
  );

  const canUserSkipPayment = useMemo(
    () => (isSuperAdmin || canSkipPayment) && isLastStep,
    [canSkipPayment, isLastStep, isSuperAdmin],
  );

  const nextStepSubmitText = useMemo(
    () =>
      t('nextStepLCW', {
        step: t(LCW_STEP_SUBMIT_TEXT[currentStep]),
      }),
    [currentStep, t],
  );

  const onClose = useOnClose();

  // PATCH REQUEST : CREATE LISTING
  const patchListingStepRequest = useCallback(
    async (values) => {
      const {
        controlledInformation,
        appointmentType,
        mediaPackage,
        buyerOutreach,
        slugName,
        ...restValues
      } = values;

      const processedValues = processLCWValuesBeforeSubmit(restValues);

      const patchValues = utils.changePropertyValueWith(
        {
          ...restValues,
          ...processedValues,
          ...(currentStep === 0 ? { type: listingType } : {}),
          ...(currentStep === 0 && checkIsUrgent(listingType)
            ? { appointmentType }
            : {}),
          ...(controlledInformation ? { controlledInformation } : {}),
          ...(currentStep === 3
            ? {
                mediaPackage: mediaPackage || null,
                buyerOutreach: buyerOutreach || null,
              }
            : {}),
        },
        null,
        undefined,
      );

      try {
        setIsSubmitting(true);

        if (values.getBuyers === GET_BUYERS_OPTIONS.PRIVATE) {
          await api.listings.setPrivateListing(listingId);

          popSuccess(t(SUCCESS_MESSAGES.LISTING_PRIVATE));
          onClose();
          return;
        }

        await patchListingStep(STEP_NAMES[listingType][currentStep], {
          ...patchValues,
          slugName,
        });

        if (PACKAGES_WITHOUT_MEDIA.includes(values.listingPackage)) {
          goToStep(CHECKOUT_STEP, { replace: true });
          return;
        }

        nextStep({ replace: true });
      } catch (e) {
        sentryError(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      currentStep,
      listingType,
      onClose,
      listingId,
      popSuccess,
      t,
      patchListingStep,
      nextStep,
      goToStep,
    ],
  );

  const { onStepSubmit } = useMultiStepFormPatching<Listing, Listing, string>({
    name: `lcw-wizard-${STEP_NAMES[listingType][currentStep]}`,
    patchRequest: patchListingStepRequest,
    onSubmit: submitListing,
    onForbiddenRequest: () => navigate(BUSINESS_ROUTE),
    replace: true,
    withRouting: false,
  });

  const submitWithoutPayment = useCallback(
    async (
      bossModeSkipPayment?: BossModeSkipPayment,
      popupMessage?: string,
    ) => {
      try {
        setIsSubmitting(true);

        mixpanelService.listingCreationInitiated(
          getMixpanelProps({
            dealId: dealRoomId,
            industry,
            isPaid: false,
            listingPackage,
            mediaPackage,
            outreach: buyerOutreach,
            listingName: listingTitle,
            dealName,
            listingType: t(getListingTypeLabel(listingType)),
          }) as ListingCreationInitiatedEventProps,
        );

        await api.listings.publishWithoutPayment(
          listingId,
          bossModeSkipPayment,
        );
        navigate(getSellingPortfolio(dealRoomId, 'listing-information'));
        popSuccess(t(popupMessage || 'submitListingSuccess'));
      } catch (e) {
        sentryError(e);
        popServerError(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      dealRoomId,
      industry,
      listingPackage,
      listingType,
      mediaPackage,
      buyerOutreach,
      listingId,
      listingTitle,
      dealName,
      getMixpanelProps,
      navigate,
      popSuccess,
      t,
      popServerError,
    ],
  );

  const onBack = useCallback(() => {
    if (
      currentStep === CHECKOUT_STEP &&
      PACKAGES_WITHOUT_MEDIA.includes(listingPackage)
    ) {
      goToStep(GET_BUYERS_STEP, { replace: true });
    } else prevStep({ replace: true });
  }, [currentStep, listingPackage, goToStep, prevStep]);

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={onStepSubmit}
      initialValues={listingFormValues}
      mutators={{ ...arrayMutators }}
      validate={validateLCW}
      render={({
        values,
        modifiedSinceLastSubmit,
        invalid,
        handleSubmit,
        form: { submit },
      }) => {
        formValues?.current !== values && (formValues.current = values);

        if (resetModified.current || modifiedSinceLastSubmit)
          modSinceLastSubmit.current = modifiedSinceLastSubmit;

        return (
          <form
            onSubmit={handleSubmit}
            className={classNames('lcw-step-wrapper', className)}
          >
            {isSubmitting && <Loader className="fixed" />}

            {children}

            {!hideControls && (
              <>
                <FloatingActions
                  disabled={isSubmitting}
                  hideSubmit={isLastStep}
                  message={t(
                    invalid ? 'formHasMissingFields' : 'formReadyToSubmit',
                  )}
                  cancelTxt={t('back')}
                  submitTxt={
                    currentStep === GET_BUYERS_STEP
                      ? t('next')
                      : nextStepSubmitText
                  }
                  onSubmit={() => scrollToErrorsOnSubmit(invalid, submit)}
                  {...(currentStep !== 0 && { onCancel: onBack })}
                >
                  {canUserSkipPayment && (
                    <Button
                      type="button"
                      disabled={invalid}
                      onClick={() =>
                        isSuperAdmin
                          ? bmSkipPaymentModal.open()
                          : submitWithoutPayment()
                      }
                    >
                      {t(isSuperAdmin ? 'bossSkipPayment' : 'skipPayment')}
                    </Button>
                  )}
                </FloatingActions>

                <BmSkipPaymentModal
                  ref={bmSkipPaymentModalRef}
                  onSuccess={submitWithoutPayment}
                />
              </>
            )}
          </form>
        );
      }}
    />
  );
};

export default LcwStepWrapper;
